import Image from "next/image";
import Link from "next/link";
import { canOptimizeExploreImage } from "../_lib/explore-image";
import type { ExploreStoryCard } from "../types";
import { STORY_BADGE_STYLES } from "./category-badge-styles";

const MATERIAL_TYPE_LABELS: Partial<
  Record<ExploreStoryCard["materialType"], string>
> = {
  PROMPT: "Prompt",
  WORKFLOW: "Workflow",
  RELEASE: "Release",
  DEAL: "Deal",
};

interface MobileStoryImageCardProps {
  story: ExploreStoryCard;
  exploreBase: string;
  priority?: boolean;
}

/**
 * Full-width image card — NYT article style.
 * Large image on top, headline below. Only "New" badge on image.
 * Category is small and muted at the bottom.
 */
export function MobileStoryImageCard({
  story,
  exploreBase,
  priority = false,
}: MobileStoryImageCardProps) {
  const primaryTag = story.productTags[0] ?? story.topicTags[0];
  const materialLabel = MATERIAL_TYPE_LABELS[story.materialType];
  const badgeStyleClass = STORY_BADGE_STYLES[story.storyBadge];
  const shouldOptimizeImage = canOptimizeExploreImage({
    src: story.coverImageUrl,
  });

  return (
    <Link
      className="group flex flex-col overflow-hidden rounded-2xl bg-[var(--explore-bg-card)] no-underline shadow-[var(--explore-card-shadow-lg)] active:scale-[0.995]"
      href={`${exploreBase}/stories/${story.storyId}`}
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
    >
      {/* Full-width image with inset */}
      <div className="relative mx-2 mt-2 aspect-[16/9] overflow-hidden rounded-t-xl bg-gradient-to-br from-[var(--explore-tag-bg)] to-[var(--explore-border-light)]">
        <Image
          alt={story.headline}
          className="h-full w-full object-cover"
          fill
          priority={priority}
          sizes="(max-width: 640px) 100vw, 50vw"
          src={story.coverImageUrl}
          unoptimized={!shouldOptimizeImage}
        />

        {/* Only "New" badge — no category on image */}
        {story.isNew && (
          <div className="absolute top-2.5 right-2.5 z-10 flex items-center gap-1.5 rounded-full bg-[var(--explore-badge-overlay-bg)] px-2.5 py-1 shadow-[var(--explore-card-shadow)] backdrop-blur-[10px]">
            <span className="h-2 w-2 animate-[explore-pulse-dot_1.5s_ease-in-out_infinite] rounded-full bg-[var(--explore-new-indicator)] shadow-[0_0_0_3px_var(--explore-new-indicator-ring)]" />
            <span className="font-bold text-[0.65rem] text-[var(--explore-text-primary)] uppercase tracking-[0.03em]">
              New
            </span>
          </div>
        )}
      </div>

      {/* Text block */}
      <div className="flex flex-col px-4 pt-3 pb-4">
        <h3
          className="mb-1.5 line-clamp-3 text-pretty font-bold text-[1.3rem] text-[var(--explore-text-primary)] leading-[1.3]"
          style={{ fontFamily: "var(--explore-card-title-stack)" }}
        >
          {story.headline}
        </h3>

        <p className="mb-2.5 line-clamp-2 text-[0.9rem] text-[var(--explore-text-secondary)] leading-[1.5]">
          {story.summary}
        </p>

        {/* Material badge → category emoji → tag → time */}
        <div className="flex flex-wrap items-center gap-1.5">
          {materialLabel && (
            <span
              className={`inline-flex items-center rounded-sm px-2 py-0.5 font-bold text-[0.65rem] uppercase tracking-[0.05em] ${badgeStyleClass}`}
            >
              {materialLabel}
            </span>
          )}
          {story.categoryEmoji && (
            <span className="inline-flex items-center justify-center rounded-sm bg-[var(--explore-tag-bg)] px-1.5 py-0.5 text-[0.7rem]">
              {story.categoryEmoji}
            </span>
          )}
          {primaryTag && (
            <span className="inline-flex items-center rounded-sm bg-[var(--explore-tag-bg)] px-2 py-0.5 font-medium text-[0.7rem] text-[var(--explore-tag-text)]">
              {primaryTag.displayName}
            </span>
          )}
          <span className="text-[0.7rem] text-[var(--explore-text-muted)]">
            {story.timeAgo}
          </span>
          <span className="text-[0.7rem] text-[var(--explore-text-muted)]">
            ·
          </span>
          <span className="text-[0.7rem] text-[var(--explore-text-muted)]">
            {story.readTime}
          </span>
        </div>
      </div>
    </Link>
  );
}
